<template>
  <div>
    <HeadSection
      :title="
        isEditMode ? 'Edit custom page settings' : 'Create new custom page'
      "
      permissionOfSection="custom-pages"
      :breadcrumbs="breadcrumbs"
    >
      <template #actions>
        <a-col :span="14" class="text-right d-flex justify-content-end">
          <div v-if="isEditMode" class="pt-4">
            <a-popconfirm
              title="Sure to delete?"
              placement="bottom"
              @confirm.stop="handleDelete"
            >
              <a-button :loading="loading" type="danger"> Delete </a-button>
            </a-popconfirm>
            <a-button
              :loading="loading"
              type="primary"
              class="ml-4"
              :disabled="hasSaved"
              @click="handleSave"
            >
              Save
            </a-button>
          </div>
          <div v-else class="pt-4">
            <a-button class="ml-4" @click="redirectToMain" :loading="loading">
              Cancel
            </a-button>
            <a-button
              :loading="loading"
              type="primary"
              class="ml-4"
              @click="handleSubmit"
            >
              Create
            </a-button>
          </div>
        </a-col>
      </template>
    </HeadSection>
    <MainContainer :loading="userLoading" permissionOfSection="custom-pages">
      <a-form-model :model="form" :rules="rules" ref="generalForm">
        <div
          class="d-flex flex-column custom-page-form"
          style="max-width: 416px"
        >
          <div class="font-weight-bold font-size-16 mb-3">General</div>

          <a-form-model-item :colon="false" prop="name" class="normal-size">
            <template #label>
              <span class="title" style="line-height: 1"> Name </span>
            </template>
            <a-input
              v-model="form.name"
              placeholder="Name"
              size="large"
              :disabled="loading"
            />
          </a-form-model-item>

          <a-form-model-item
            prop="segments"
            required
            :colon="false"
            style="max-width: 427px"
          >
            <template #label>
             <SegmentsLabel />
            </template>
            <div class="d-flex align-items-center w-full">
              <SegmentsSelect v-model="form.segments" :segments="segments" :loading="loading" />
            </div>
          </a-form-model-item>

          <div style="display: flex; justify-content: space-between">
            <a-form-model-item
              ref="start-period"
              prop="period.start"
              :colon="false"
              label="Start date"
            >
              <a-date-picker
                v-model="form.period.start"
                :disabled="loading"
                :disabled-date="disabledStartDate"
                format="YYYY-MM-DD"
                size="large"
                :class="{
                  'activity-periods__expired-period':
                    form.period.end?.isBefore(),
                }"
                @focus="setDisabledStartDateValue"
                @change="setDisabledStartDateValue"
              />
            </a-form-model-item>
            <a-form-model-item
              ref="end-period"
              prop="period.end"
              :colon="false"
              label="End date"
            >
              <a-date-picker
                v-model="form.period.end"
                :disabled-date="disabledEndDate"
                format="YYYY-MM-DD"
                size="large"
                placeholder="End date"
                :disabled="loading"
                class="hover-date-picker"
                :class="{
                  'activity-periods__expired-period':
                    form.period.end?.isBefore(),
                }"
                @focus="setDisabledEndDateValue"
                @change="setDisabledEndDateValue"
              />
            </a-form-model-item>
          </div>

          <a-form-model-item
            prop="apps"
            required
            :colon="false"
            class="d-block"
          >
            <template #label>
              <span class="mr-4">Apps</span>
              <a
                style="color: rgb(0, 123, 255) !important; font-weight: 400"
                @click.stop="openAppsModal"
              >
                + Add
              </a>
            </template>
            <div v-if="form.apps.length">
              <a-tag
                v-for="app in form.apps?.slice(0, 5)"
                :key="app.id"
                style="margin-right: 15px; padding: 1px 8px; border-radius: 2px"
              >
                {{ app.name }}
                <a-icon
                  v-if="app.platform.toLowerCase() === 'android'"
                  type="android"
                  class="ml-1 text-success"
                />
                <a-icon
                  v-if="app.platform.toLowerCase() === 'ios'"
                  type="apple"
                  class="ml-1 text-primary"
                />
                <a-icon
                  v-if="app.platform.toLowerCase() === 'web'"
                  type="global"
                  class="ml-1 text-default"
                />
              </a-tag>
              <a-tag v-if="form.apps.length > 4"> ... </a-tag>
            </div>
          </a-form-model-item>
        </div>
      </a-form-model>
    </MainContainer>

    <a-modal
      v-model="modalAppsOpen"
      title="Add new apps"
      :footer="null"
      width="700px"
      :destroyOnClose="true"
    >
      <AppsModal
        @selectApps="handleSelectedApps"
        @close="closeAppsModal"
        :modalAppsData="form.apps"
        :scroll="{ y: 450 }"
      />
    </a-modal>
  </div>
</template>
<script>
import AppsModal from '@/components/custom/modals/appsModal.vue'
import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'
import SegmentsSelect from '@/components/custom/segments/segmentsSelect.vue'
import SegmentsLabel from '@/components/custom/segments/segmentsLabel.vue'
import apiClient from '@/services/axios'

import { mapState, mapGetters, mapActions } from 'vuex'
import isEqual from 'lodash/isEqual'

export default {
  components: {
    AppsModal,
    HeadSection,
    MainContainer,
    SegmentsSelect,
    SegmentsLabel,
  },
  data() {
    return {
      rules: {
        name: [{ required: true, message: 'Enter a name', trigger: 'blur' }],
        segments: [
          { required: true, message: 'Add segments', trigger: 'change' },
        ],
        apps: [{ required: true, message: 'Add apps', trigger: 'change' }],
        'period.start': [
          { required: true, message: 'Enter a start date', trigger: 'blur' },
        ],
        'period.end': [
          { required: true, message: 'Enter an end date', trigger: 'blur' },
        ],
      },
      previousDate: null,
      endDate: null,
      modalAppsOpen: false,
      hasSaved: true,
    }
  },
  computed: {
    ...mapState(['user']),
    ...mapGetters([
      'fetchedData',
      'fetchedDataLoading',
      'isPageFilled',
      'isPublished',
    ]),
    ...mapGetters('general', ['form', 'segments', 'isFormFilled']),
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    loading() {
      return this.userLoading || this.fetchedDataLoading
    },
    currentUUID() {
      return this.$route.params.id ?? ''
    },
    isEditMode() {
      return !!this.currentUUID
    },
    breadcrumbs() {
      return [
        { name: 'Сustom pages', link: '/custom-pages' },
        this.isEditMode && {
          name: 'Constructor',
          link: `/custom-pages/constructor/${this.currentUUID}`,
        },
        {
          name: this.isEditMode
            ? 'Edit custom page settings'
            : 'Create new custom page',
          link: undefined,
        },
      ].filter(Boolean)
    },
    savedFormData() {
      if (!this.fetchedData) {
        return null
      }

      return {
        name: this.fetchedData?.name,
        segments: this.fetchedData?.segments?.map((s) => s.id),
        period: {
          start: this.$moment.utc(this.fetchedData?.period?.starts_at),
          end: this.$moment.utc(this.fetchedData?.period?.ends_at),
        },
        apps: JSON.parse(JSON.stringify(this.fetchedData?.applications)),
      }
    },
  },
  methods: {
    ...mapActions(['createPayload', 'fetchData', 'initPage']),
    ...mapActions('general', ['fetchSegments', 'fillForm']),
    async handleSave() {
      const payload = await this.createPayload()

      const url = `/admin/custom-page/${this.currentUUID}`

      try {
        const resp = await apiClient.put(url, payload)

        if (!resp) {
          throw new Error()
        }

        if (resp.status !== 200) {
          // eslint-disable-next-line no-throw-literal
          throw { resp }
        }

        this.$notification.success({
          message: 'Custom page changes saved',
        })

        await this.initPage(this.currentUUID)

        this.hasSaved = true
      } catch (e) {
        this.$notification.error({
          message: 'Error while upading custom page',
          description: e.message,
        })
        console.error('Error upading custom page:', e)
      }
    },
    async handleSubmit() {
      this.$refs.generalForm.validate(async (valid) => {
        if (valid) {
          await this.$store.dispatch('general/submitForm', {
            moment: this.$moment,
            router: this.$router,
            notify: ({ message }) => {
              this.$notification.error({ message })
            },
          })
        } else {
          console.log('form is not valid')
        }
      })
    },
    redirectToMain() {
      return this.$router.push(this.breadcrumbs?.[0].link)
    },
    openSegmentsModal() {
      this.modalSegmentsOpen = true
    },
    closeSegmentsModal() {
      this.modalSegmentsOpen = false
    },
    handleAddSegment(newSegment) {
      this.closeSegmentsModal()
      this.$store.commit('general/ADD_SEGMENT', newSegment)
    },
    onSegmentsChange() {
      this.$refs.generalForm.validateField('segments')
    },
    openAppsModal() {
      this.modalAppsOpen = true
    },
    closeAppsModal() {
      this.modalAppsOpen = false
    },
    handleSelectedApps(apps) {
      this.$store.commit('general/SET_FORM_FIELD', {
        field: 'apps',
        value: JSON.parse(JSON.stringify(apps)),
      })
      this.$refs.generalForm.validateField('apps')

      this.closeAppsModal()
    },
    setDisabledStartDateValue() {
      if (this.form.period.start) {
        this.clearValidate()
      }
    },
    setDisabledEndDateValue() {
      if (this.form.period.end) {
        this.clearValidate()
      }
    },
    disabledStartDate(day) {
      const start = this.form.period.start
      const end = this.form.period.end
      const d = day.clone().startOf('day')

      if (start && end) {
        if (start.toString() === end.toString()) {
          return d.isSameOrBefore(end.clone().endOf('day'))
        }

        return (
          d.isSameOrAfter(end.clone().endOf('day')) ||
          d.isSameOrBefore(start.clone().startOf('day'))
        )
      } else if (end) {
        return d.isSameOrAfter(end.clone().endOf('day'))
      } else if (start) {
        return d.isSameOrBefore(start.clone().startOf('day'))
      }

      return false
    },
    disabledEndDate(day) {
      const start = this.form.period.start
      const end = this.form.period.end
      const d = day.clone().endOf('day')

      if (start && end) {
        if (start.toString() === end.toString()) {
          return d.isSameOrBefore(end.clone().endOf('day'))
        }

        return d.isSameOrBefore(start.clone().startOf('day'))
      } else if (start) {
        return d.isSameOrBefore(start.clone().startOf('day'))
      } else if (end) {
        return d.isSameOrAfter(end.clone().endOf('day'))
      }

      return false
    },
    clearValidate() {
      this.$refs['start-period'].clearValidate()
      this.$refs['end-period'].clearValidate()
    },
    async handleDelete() {
      const url = `/admin/custom-page/${this.currentUUID}`

      try {
        await apiClient.delete(url)

        this.$notification.success({
          message: 'Custom page successfully deleted',
        })

        this.$router.push('/custom-pages')
      } catch (e) {
        this.$notification.error({ message: 'Could not delete custom page' })
      }
    },
  },
  async created() {
    if (!this.segments?.length) {
      await this.fetchSegments()
    }

    if (this.isEditMode) {
      if (!this.isPageFilled) {
        await this.initPage(this.currentUUID)
      }

      await this.fetchData(this.currentUUID)
      this.fillForm(this.fetchedData)
    }
  },
  beforeRouteLeave(to, from, next) {
    if (!to.name.startsWith('edit-custom-page')) {
      this.$store.dispatch('resetState')
    }

    next()
  },
  watch: {
    form: {
      handler(val) {
        if (!this.isEditMode) {
          return
        }

        const isPageReady = this.isPageFilled && this.isFormFilled

        if (isPageReady && !isEqual(val, this.savedFormData)) {
          this.hasSaved = false
        }
      },
      deep: true,
    },
  },
}
</script>
